/**
 * components/client/ThankYouScreen.tsx
 *
 * Animated thank-you screen displayed after successful form submission.
 * Features: animated check icon, confetti burst particles, logo display.
 * No date/time display (per UX guidelines).
 *
 * Updated Feb 2026: Logo repositioned above checkmark, increased size.
 * Updated Feb 2026: Confetti rotation moved to --rotate CSS custom property
 *   so rotate() is applied inside the keyframe alongside translate/scale.
 * Updated Feb 2026: Replaced global React namespace type assertion with
 *   explicit CSSProperties import for self-contained typing.
 * Updated Apr 2026: ClientBrandingHeader replaces raw logo img; senderDisplayName
 *   added to the confirmation message for explicit attribution.
 * Updated Apr 2026: header now only rendered when logoUrl is present - senderDisplayName
 *   already appears in the confirmation message, avoiding visual duplication.
 * Updated Apr 2026: logo size changed from "md" to "lg" to match the WelcomeScreen (h-20 md:h-24).
 */

import type { CSSProperties } from 'react';
import { CheckCircle2 } from 'lucide-react';
import ClientBrandingHeader from '../branding/ClientBrandingHeader';

interface ConfettiPiece {
  tx: string;
  ty: string;
  color: string;
  delay: string;
  rotate: string;
}

const CONFETTI: ConfettiPiece[] = [
  { tx: '-80px', ty: '-70px', color: '#2563eb', delay: '0.1s', rotate: '45deg' },
  { tx: '80px',  ty: '-70px', color: '#22c55e', delay: '0.15s', rotate: '-30deg' },
  { tx: '-110px', ty: '10px', color: '#f59e0b', delay: '0.2s', rotate: '20deg' },
  { tx: '110px',  ty: '10px', color: '#ef4444', delay: '0.1s', rotate: '-45deg' },
  { tx: '-70px',  ty: '85px', color: '#0ea5e9', delay: '0.25s', rotate: '60deg' },
  { tx: '70px',   ty: '85px', color: '#f59e0b', delay: '0.18s', rotate: '-20deg' },
  { tx: '15px',   ty: '-95px', color: '#22c55e', delay: '0.05s', rotate: '10deg' },
  { tx: '-15px',  ty: '100px', color: '#2563eb', delay: '0.3s', rotate: '-60deg' },
];

interface ThankYouScreenProps {
  logoUrl: string | null;
  senderDisplayName: string | null;
}

export default function ThankYouScreen({ logoUrl, senderDisplayName }: ThankYouScreenProps) {
  return (
    <div className="flex-1 flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md text-center">
        {logoUrl && (
          <div className="flex justify-center mb-8 animate-welcome-enter">
            <ClientBrandingHeader
              logoUrl={logoUrl}
              displayName={null}
              size="lg"
            />
          </div>
        )}

        <div className="relative inline-block mb-8">
          {CONFETTI.map((c, i) => (
            <div
              key={i}
              className="animate-confetti"
              style={{
                '--tx': c.tx,
                '--ty': c.ty,
                '--rotate': c.rotate,
                backgroundColor: c.color,
                animationDelay: c.delay,
                left: 'calc(50% - 4px)',
                top: 'calc(50% - 4px)',
              } as CSSProperties}
            />
          ))}

          <div className="relative z-10 w-24 h-24 bg-success-100 rounded-full flex items-center justify-center animate-check-pop">
            <CheckCircle2 className="w-12 h-12 text-success-600" />
          </div>
        </div>

        <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4 animate-thank-you-text-1">
          Merci !
        </h2>

        <p className="text-lg text-neutral-600 leading-relaxed animate-thank-you-text-2">
          {senderDisplayName ? (
            <>
              Vos informations ont été transmises à{' '}
              <span className="font-semibold text-neutral-700">{senderDisplayName}</span>
              {' '}avec succès.
            </>
          ) : (
            'Vos informations ont été transmises avec succès.'
          )}
          <br />
          Vous pouvez maintenant fermer cette page.
        </p>
      </div>
    </div>
  );
}
